import { useEffect, useState } from "react";
import { Calculator, Save, Lock } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import MoneyField from "./MoneyField";
import { TimeInput24 } from "./DateTime24";
import { useAuth } from "../lib/auth";
import {
  fetchManualTowPricing,
  updateManualTowPricing,
} from "../lib/api";

/**
 * Manual Tow Pricing — Settings → Integrations card.
 *
 * Mirrors the gear-icon dialog on /tow-calc so admins can manage rates
 * from either place. All money fields are ex-GST — GST is added on top
 * in the final quote. GST stored as 0-1 on the server but surfaced as
 * 0-100% in the UI. Business hours surfaced as HH:MM time pickers.
 */

const MONEY_FIELDS = [
  { k: "hourly_rate", label: "Hourly rate ($/hr, excl. GST)", step: "0.01", hint: "Charged over drive + load/unload time." },
  { k: "after_hours_surcharge", label: "After-hours surcharge ($, excl. GST)", step: "0.01", hint: "Flat $ added outside business hours." },
  { k: "faf_per_km", label: "FAF per km ($, excl. GST)", step: "0.001", hint: "Fuel Adjustment Factor charged on every road km." },
  { k: "per_km_rate", label: "Per-km rate ($, excl. GST)", step: "0.001", hint: "Optional extra on top of hourly (0 = ignore)." },
  { k: "minimum_charge", label: "Minimum charge ($, excl. GST)", step: "0.01", hint: "Safety floor. Effective minimum = max(1h × hourly rate, this value). Any after-hours surcharge is added on top." },
  { k: "heavy_vehicle_surcharge", label: "Heavy vehicle surcharge ($, excl. GST)", step: "0.01", hint: "Flat $ added when the Tow Calc 'Heavy vehicle' tick box is on. Admin fills in — defaults $0 (opt-in)." },
];

const _toHHMM = (h) => `${String(Number(h) || 0).padStart(2, "0")}:00`;
const _fromHHMM = (s) => {
  const [h] = String(s || "").split(":");
  return Number(h) || 0;
};

const _decorate = (raw) => ({
  ...raw,
  _gst_percent: Number((Number(raw.gst_rate ?? 0.15) * 100).toFixed(2)),
  _business_start: _toHHMM(raw.business_hours_start_hour ?? 8),
  _business_end: _toHHMM(raw.business_hours_end_hour ?? 17),
});

export default function ManualTowPricingCard() {
  const { isAdmin } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchManualTowPricing()
      .then((raw) => setCfg(_decorate(raw)))
      .catch(() => toast.error("Failed to load Manual Tow pricing"));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        ...Object.fromEntries(MONEY_FIELDS.map((f) => [f.k, Number(cfg[f.k])])),
        load_unload_minutes: Number(cfg.load_unload_minutes),
        travel_time_correction_pct: Number(cfg.travel_time_correction_pct ?? 0),
        bill_time_rounding_minutes: Number(cfg.bill_time_rounding_minutes ?? 0),
        gst_rate: Number(cfg._gst_percent) / 100,
        business_hours_start_hour: _fromHHMM(cfg._business_start),
        business_hours_end_hour: _fromHHMM(cfg._business_end),
      };
      if (patch.gst_rate < 0 || patch.gst_rate > 1) {
        toast.error("GST % must be between 0 and 100");
        setSaving(false);
        return;
      }
      const next = await updateManualTowPricing(patch);
      setCfg(_decorate(next));
      toast.success("Manual Tow rates saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) {
    return (
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-5 bg-white dark:bg-slate-900 text-sm text-slate-500">
        Loading manual tow pricing…
      </div>
    );
  }

  return (
    <div
      className="rounded-lg border border-slate-200 dark:border-slate-700 p-5 bg-white dark:bg-slate-900"
      data-testid="manual-pricing-card"
    >
      <div className="flex items-center gap-2 mb-2">
        <Calculator className="h-5 w-5 text-emerald-600" />
        <h2 className="text-lg font-semibold">Manual Tow Pricing</h2>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Powers the Tow Calc (both the desktop <code>/tow-calc</code> page and
        the on-call driver's Price Check). All money values are{" "}
        <strong>excl. GST</strong>; GST is added on top in the final quote.
        Does NOT affect AA or First Rescue pricing — those have their own
        cards above.
      </p>

      {!isAdmin && (
        <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900/40 text-amber-800 dark:text-amber-200 p-3 text-xs flex items-center gap-2 mb-4">
          <Lock className="h-3.5 w-3.5" />
          You can view these rates. Ask an admin to change them.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {MONEY_FIELDS.map((f) => (
          <MoneyField
            key={f.k}
            id={`manual-card-${f.k}`}
            label={f.label}
            hint={f.hint}
            step={f.step}
            decimals={f.step === "0.001" ? 3 : 2}
            prefix={null}
            value={cfg[f.k] ?? ""}
            onChange={(v) => setCfg({ ...cfg, [f.k]: v })}
            disabled={!isAdmin}
            testid={`manual-card-${f.k}`}
          />
        ))}

        <div>
          <Label htmlFor="manual-card-load_unload_minutes" className="text-xs">
            Load/unload (min)
          </Label>
          <Input
            id="manual-card-load_unload_minutes"
            type="number"
            step="1"
            value={cfg.load_unload_minutes ?? ""}
            onChange={(e) =>
              setCfg({ ...cfg, load_unload_minutes: e.target.value })
            }
            disabled={!isAdmin}
            data-testid="manual-card-load_unload_minutes"
          />
          <p className="text-[11px] text-slate-400 mt-0.5">
            Added to drive time when billing hourly.
          </p>
        </div>

        <div>
          <Label htmlFor="manual-card-travel_time_correction_pct" className="text-xs">
            Travel time correction (%)
          </Label>
          <Input
            id="manual-card-travel_time_correction_pct"
            type="number"
            step="0.5"
            min="0"
            max="100"
            value={cfg.travel_time_correction_pct ?? ""}
            onChange={(e) =>
              setCfg({ ...cfg, travel_time_correction_pct: e.target.value })
            }
            disabled={!isAdmin}
            data-testid="manual-card-travel_time_correction_pct"
          />
          <p className="text-[11px] text-slate-400 mt-0.5">
            Route times are free-flow car times. Add a % for traffic, lights
            and a loaded truck. Applies to drive time only — never to km or FAF.
          </p>
        </div>

        <div>
          <Label htmlFor="manual-card-bill_time_rounding_minutes" className="text-xs">
            Bill time in blocks of (min)
          </Label>
          <Input
            id="manual-card-bill_time_rounding_minutes"
            type="number"
            step="1"
            min="0"
            max="60"
            value={cfg.bill_time_rounding_minutes ?? ""}
            onChange={(e) =>
              setCfg({ ...cfg, bill_time_rounding_minutes: e.target.value })
            }
            disabled={!isAdmin}
            data-testid="manual-card-bill_time_rounding_minutes"
          />
          <p className="text-[11px] text-slate-400 mt-0.5">
            Billable time rounds to the nearest block (0 = exact minutes).
          </p>
        </div>

        <div>
          <Label htmlFor="manual-card-gst_percent" className="text-xs">
            GST rate (%)
          </Label>
          <Input
            id="manual-card-gst_percent"
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={cfg._gst_percent ?? ""}
            onChange={(e) =>
              setCfg({ ...cfg, _gst_percent: e.target.value })
            }
            disabled={!isAdmin}
            data-testid="manual-card-gst_percent"
          />
          <p className="text-[11px] text-slate-400 mt-0.5">
            NZ default is 15%.
          </p>
        </div>

        <div>
          <Label htmlFor="manual-card-business_start" className="text-xs">
            Business hours start
          </Label>
          <TimeInput24
            value={cfg._business_start ?? "08:00"}
            onChange={(v) => setCfg({ ...cfg, _business_start: v })}
            disabled={!isAdmin}
            dataTestId="manual-card-business_start"
          />
        </div>

        <div>
          <Label htmlFor="manual-card-business_end" className="text-xs">
            Business hours end
          </Label>
          <TimeInput24
            value={cfg._business_end ?? "17:00"}
            onChange={(v) => setCfg({ ...cfg, _business_end: v })}
            disabled={!isAdmin}
            dataTestId="manual-card-business_end"
          />
          <p className="text-[11px] text-slate-400 mt-0.5">
            After-hours = outside this window. The on-call schedule
            (Settings → On-call) actually drives the surcharge on live
            quotes.
          </p>
        </div>
      </div>

      {isAdmin && (
        <div className="mt-5 flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="manual-card-save-btn">
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Saving…" : "Save rates"}
          </Button>
        </div>
      )}
    </div>
  );
}
